import React, { useEffect, useRef } from "react";
import { Input, Select, Text } from "components-lib";
import EnhancedAutocompleteInput from "../EnhancedAutocompleteInput";
import { useDoctorRecommendations } from "../hooks/useDoctorRecommendations";
import {
  saveToStorage,
  getStoredSuggestions,
  STORAGE_KEYS,
} from "../utils/storageUtils";

/**
 * DiagnosisSection Component
 * Manages patient diagnosis information with duration and date tracking
 * Provides enhanced autocomplete functionality for diagnosis entries
 * Fetches suggestions from API and allows custom input
 */
const DiagnosisSection = ({
  templateData,
  diagnosiss,
  setDiagnosiss,
  newDiagnosis,
  setNewDiagnosis,
  diagnosisSuggestions,
  setDiagnosisSuggestions,
  doctorRecommendations,
  doctor_id,
  appointmentId,
  appointmentPrescription,
}) => {
  // Use the doctor recommendations hook for API-based suggestions
  const { recommendations } = useDoctorRecommendations(doctor_id);

  const hydratedFromPrescriptionRef = useRef(null);

  // Handle diagnosis selection from autocomplete
  const handleDiagnosisSelect = (value, metadata) => {
    setNewDiagnosis((prev) => ({
      ...prev,
      diagnosis: value,
    }));

    // Save to localStorage for future suggestions
    saveToStorage(STORAGE_KEYS.DIAGNOSIS, value);
  };

  // Handle diagnosis input change
  const handleDiagnosisChange = (e) => {
    const value = e.target.value;
    setNewDiagnosis((prev) => ({
      ...prev,
      diagnosis: value,
    }));
  };

  // Add new diagnosis to the list
  const handleAddDiagnosis = () => {
    if (newDiagnosis.diagnosis) {
      setDiagnosiss([
        ...diagnosiss,
        {
          ...newDiagnosis,
          no: diagnosiss.length + 1,
          duration: "", // Keep for backward compatibility
          date: "", // Keep for backward compatibility
        },
      ]);
      saveToStorage(STORAGE_KEYS.DIAGNOSIS, newDiagnosis.diagnosis);
      setNewDiagnosis({ no: "", diagnosis: "", duration: "", date: "" });
      setDiagnosisSuggestions([]);
    }
  };

  // Delete diagnosis from the list
  const handleDeleteDiagnosis = (index) => {
    setDiagnosiss(diagnosiss.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!appointmentId || !appointmentPrescription) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      const source = appointmentPrescription.data || appointmentPrescription;
      const apiDiagnoses = Array.isArray(source?.diagnosis)
        ? source.diagnosis
        : Array.isArray(source?.prescription?.diagnosis)
          ? source.prescription.diagnosis
          : [];

      apiDiagnoses.forEach((apiDiagnosis) => {
        const diagText = apiDiagnosis?.diagnosis;
        if (
          diagText &&
          !diagnosiss.some(
            (existingDiagnosis) =>
              (existingDiagnosis.diagnosis || "").toLowerCase() ===
              diagText.toLowerCase(),
          )
        ) {
          const newDiagnosisEntry = {
            no: apiDiagnosis.diagnosis_no,
            diagnosis: diagText.trim(),
          };

          setDiagnosiss((prevDiagnosiss = []) => [
            ...prevDiagnosiss,
            newDiagnosisEntry,
          ]);

          saveToStorage(
            STORAGE_KEYS.DIAGNOSIS,
            diagText.trim(),
          );
        }
      });

      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error(
        `Error hydrating diagnosis for appointment ${appointmentId}:`,
        error,
      );
    }
  }, [appointmentId, appointmentPrescription, diagnosiss, setDiagnosiss]);

  useEffect(() => {
    if (!templateData) return;

    try {
      let parsed = templateData;
      if (typeof templateData === "string") {
        parsed = JSON.parse(templateData);
      }

      const templateDiagnosis = Array.isArray(parsed.diagnosis)
        ? parsed.diagnosis
        : Array.isArray(parsed)
        ? parsed
        : [];

      console.log(" Template diaganosis are", templateDiagnosis);
      const normalized = templateDiagnosis
        .map((d) => {
          const no = d?.diagnosis_no ?? d?.no ?? d?.id ?? undefined;
          const diagnosis =
        d?.diagnosis !== null && d?.diagnosis !== undefined
          ? String(d.diagnosis).trim()
          : "";
          return {
        no,
        diagnosis,
        duration: d?.duration ?? "",
        date: d?.date ?? "",
          };
        })
        // keep entries that either have a diagnosis text OR have an identifier (diagnosis_no/id)
        .filter((d) => (d.diagnosis && d.diagnosis.length > 0) || d.no !== undefined);

      if (normalized.length === 0) return;

      console.log("Normalized Template diaganosis are", normalized);

      setDiagnosiss((prev = []) => {
        const existing = Array.isArray(prev) ? prev : [];

        const toAdd = [];

        const normalizeStr = (s) =>
          typeof s === "string" ? s.trim() : "";

        const isDuplicateAgainst = (candidate, target) => {
          const cand = normalizeStr(candidate.diagnosis);
          const targ = normalizeStr(target.diagnosis);
          if (!cand || !targ) return false;
          return cand.toLowerCase() === targ.toLowerCase();
        };

        normalized.forEach((td) => {
          const diagText = normalizeStr(td.diagnosis);
          // do not add anything if diagnosis is null/empty
          if (!diagText) return;

          const existsInExisting = existing.some((ex) =>
        isDuplicateAgainst(td, ex)
          );
          const existsInToAdd = toAdd.some(
        (added) =>
          normalizeStr(added.diagnosis).toLowerCase() ===
          diagText.toLowerCase()
          );

          if (!existsInExisting && !existsInToAdd) {
        toAdd.push({
          ...td,
          no: td.no || `${existing.length + toAdd.length + 1}`, // fallback numeric no
        });
          }
        });

        if (toAdd.length === 0) return existing;

        // clear newDiagnosis similar to API flow
        setNewDiagnosis({
          diagnosis: "",
          no: "",
          duration: "",
          date: "",
        });

        return [...existing, ...toAdd];
      });
    } catch (err) {
      console.warn("Failed to parse/apply templateData diagnosis:", err);
    }
  }, [templateData, setDiagnosiss, setNewDiagnosis]);

  return (
    <tr>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <Text fontWeight="bold" style={{ fontSize: "16px" }}>
          Diagnosis:
        </Text>
      </td>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        {/* Diagnosis Input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          <EnhancedAutocompleteInput
            value={newDiagnosis.diagnosis}
            onChange={handleDiagnosisChange}
            onSelect={handleDiagnosisSelect}
            placeholder="Enter diagnosis..."
            suggestions={[
              ...recommendations.diagnosis,
              ...getStoredSuggestions(STORAGE_KEYS.DIAGNOSIS),
            ]}
            allowCustomInput={true}
            maxSuggestions={8}
            minInputLength={1}
            highlightMatch={true}
            onCustomInput={(value) =>
              saveToStorage(STORAGE_KEYS.DIAGNOSIS, value)
            }
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #E2E8F0",
            }}
          />

          <button
            style={{
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              padding: "8px 16px",
              fontSize: "14px",
              whiteSpace: "nowrap",
            }}
            onClick={handleAddDiagnosis}
            disabled={!newDiagnosis?.diagnosis?.toString?.()?.trim()}
          >
            Add
          </button>
        </div>

        {/* Selected Diagnoses Container */}
        {diagnosiss.length > 0 && (
          <div
            style={{
              border: "none",
              borderRadius: "10px",
              padding: "12px",
              backgroundColor: "#F8FAFC",
              marginTop: "12px",
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
            }}
          >
            <Text
              fontWeight="bold"
              style={{ fontSize: "14px", marginBottom: "8px" }}
            >
              Diagnosis:
            </Text>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {diagnosiss.map((diag, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#E2E8F0",
                    borderRadius: "10px",
                    padding: "10px 14px",
                    fontSize: "14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <span>{diag.diagnosis}</span>
                  </div>
                  <button
                    style={{
                      backgroundColor: "#CBD5E1",
                      color: "red",
                      fontWeight: "bold",
                      fontSize: "14px",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      padding: "4px 8px",
                      // fontSize: "12px",
                      lineHeight: "1",
                      minWidth: "20px",
                      height: "20px",
                    }}
                    onClick={() => handleDeleteDiagnosis(index)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};
export default DiagnosisSection;
